import { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Download, X, Clock, Share, Smartphone } from 'lucide-react';

const STORAGE_KEY = 'siteflow.install.snoozeUntil';
const DISMISS_DAYS = 30;
const REMIND_DAYS = 3;
const FIRST_SHOW_DELAY_MS = 2000; // let the login page settle first

function now() { return Date.now(); }
function daysFromNow(days) { return now() + days * 24 * 60 * 60 * 1000; }

function snoozeUntil(ts) {
  try { localStorage.setItem(STORAGE_KEY, String(ts)); } catch { /* ignore */ }
}

function isSnoozed() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (!v) return false;
    return Number(v) > now();
  } catch { return false; }
}

function isStandalone() {
  if (typeof window === 'undefined') return false;
  const mqStandalone = window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  // iOS Safari reports via navigator.standalone
  const iosStandalone = typeof window.navigator !== 'undefined' && window.navigator.standalone === true;
  return Boolean(mqStandalone || iosStandalone);
}

function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOS = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ reports as Mac with touch — detect that too
  const iPadOS = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return iOS || iPadOS;
}

function isSafari() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS/.test(ua);
}

export default function InstallAppPrompt() {
  const [visible, setVisible] = useState(false);
  const [bipEvent, setBipEvent] = useState(null); // beforeinstallprompt event
  const [showIosSheet, setShowIosSheet] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed, never show
    if (isSnoozed()) return;

    let timer;
    const onBip = (e) => {
      e.preventDefault();
      setBipEvent(e);
      timer = setTimeout(() => setVisible(true), FIRST_SHOW_DELAY_MS);
    };
    window.addEventListener('beforeinstallprompt', onBip);

    // Installed handler — if the app gets installed, hide and remember
    const onInstalled = () => {
      setVisible(false);
      snoozeUntil(daysFromNow(365 * 5));
    };
    window.addEventListener('appinstalled', onInstalled);

    // iOS Safari: no beforeinstallprompt — show the manual hint after a short delay
    if (isIOS() && isSafari()) {
      timer = setTimeout(() => setVisible(true), FIRST_SHOW_DELAY_MS);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.removeEventListener('appinstalled', onInstalled);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (bipEvent) {
      try {
        bipEvent.prompt();
        const { outcome } = await bipEvent.userChoice;
        if (outcome === 'accepted') {
          snoozeUntil(daysFromNow(365 * 5));
        } else {
          snoozeUntil(daysFromNow(REMIND_DAYS));
        }
      } catch {
        snoozeUntil(daysFromNow(REMIND_DAYS));
      } finally {
        setBipEvent(null);
        setVisible(false);
      }
      return;
    }
    // iOS path — just open the how-to sheet
    setShowIosSheet(true);
  }, [bipEvent]);

  const handleRemind = useCallback(() => {
    snoozeUntil(daysFromNow(REMIND_DAYS));
    setVisible(false);
  }, []);

  const handleDismiss = useCallback(() => {
    snoozeUntil(daysFromNow(DISMISS_DAYS));
    setVisible(false);
  }, []);

  if (!visible) return null;
  const isIosHint = isIOS() && isSafari() && !bipEvent;

  return (
    <>
      <div
        role="dialog"
        aria-label="Install SiteFlow"
        data-testid="install-prompt"
        className="fixed inset-x-0 bottom-0 z-[60] sm:bottom-4 sm:inset-x-auto sm:right-4 sm:max-w-sm sm:w-full px-3 pb-3 sm:p-0"
      >
        <div className="bg-white border border-slate-200 rounded-2xl shadow-lg p-4 flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
            <Smartphone className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Install SiteFlow for faster access on site
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              {isIosHint
                ? 'Add it to your home screen for one-tap opening, even offline.'
                : 'Works offline for read-only viewing. One-tap install.'}
            </p>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <Button
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 h-8 text-xs"
                onClick={handleInstall}
                data-testid="install-prompt-install"
              >
                <Download className="h-3.5 w-3.5 mr-1" />
                {isIosHint ? 'Show me how' : 'Install'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 text-xs text-slate-500"
                onClick={handleRemind}
                data-testid="install-prompt-remind"
              >
                <Clock className="h-3.5 w-3.5 mr-1" />
                Remind me later
              </Button>
            </div>
          </div>
          <button
            type="button"
            aria-label="Dismiss install prompt"
            onClick={handleDismiss}
            className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex-shrink-0"
            data-testid="install-prompt-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {showIosSheet && (
        <div
          className="fixed inset-0 z-[70] bg-black/40 flex items-end sm:items-center justify-center p-4"
          onClick={() => setShowIosSheet(false)}
          data-testid="ios-install-sheet"
        >
          <div
            className="bg-white rounded-2xl max-w-sm w-full p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center">
                <Smartphone className="h-4 w-4 text-white" />
              </div>
              <h3 className="font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>Add SiteFlow to Home Screen</h3>
            </div>
            <ol className="text-sm text-slate-700 space-y-3 pl-1">
              <li className="flex gap-2">
                <span className="h-5 w-5 rounded-full bg-blue-100 text-blue-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0">1</span>
                <span>Tap the <Share className="inline h-3.5 w-3.5 mx-0.5 align-text-top" /><b>Share</b> button at the bottom of Safari.</span>
              </li>
              <li className="flex gap-2">
                <span className="h-5 w-5 rounded-full bg-blue-100 text-blue-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0">2</span>
                <span>Scroll and tap <b>Add to Home Screen</b>.</span>
              </li>
              <li className="flex gap-2">
                <span className="h-5 w-5 rounded-full bg-blue-100 text-blue-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0">3</span>
                <span>Tap <b>Add</b> in the top right.</span>
              </li>
            </ol>
            <div className="pt-2 flex justify-end">
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => { setShowIosSheet(false); handleRemind(); }} data-testid="ios-install-got-it">
                Got it
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
